import axios from 'helpers/axios';
import { ValidationPattern } from 'helpers/form';
import { useCompany, useOpenState, useToasts } from 'hooks';
import _ from 'lodash';
import { defaultJobSettings } from 'providers/company';
import React from 'react';
import ReactDom from 'react-dom';
import { AsyncSelect, IconButton, JobRules } from 'ui/components/shared';
import {
  Button,
  Container,
  Form,
  Label,
  Modal,
  ModalPosition,
  MultiTextInput,
  Select,
  TextInput,
  Title,
  ToggleSwitch,
} from 'ui/components/styled';
import { AddCategory, AddCustomer } from '../forms';
import { MenuFooter } from './styled';

// ToDo: Rename job(s) to monitor(s)
// This have been partially done on the frontend, but still:
// - source code
// - routes
// - backend
// - database collection
// - env. variables
// that have to be renamed as well

export const AddJob = ({ customer, onClose, onSuccess, ...rest }) => {
  const { activeCompany, getActiveCompanySettings, fetchCompanyCustomers, fetchCompanyCategories } =
    useCompany();
  const initialCustomer = React.useMemo(() => customer?.id || '', [customer]);
  const [state, setState] = React.useState(() => {
    const settings = getActiveCompanySettings();
    return {
      name: '',
      customer: initialCustomer,
      category: '',
      sender: { email: '' },
      rules: {},
      rulesLogic: { passing: 'and', failing: 'and', warning: 'and' },
      hoursUntilWarning: settings?.hoursUntilWarning || '',
      emailCapacity: settings?.emailCapacity || '',
      notifications: {
        enabled: settings?.notifications?.enabled ?? true,
        receivers: settings?.notifications?.receivers || [],
      },
    };
  });
  const [customers, setCustomers] = React.useState([]);
  const [categories, setCategories] = React.useState([]);
  const [duplicateFrom, setDuplicateForm] = React.useState('');
  const [preventSubmit, setPreventSubmit] = React.useState(false);
  const [modal, openModal, closeModal] = useOpenState();
  const { errorToast, successToast, removeAllToasts } = useToasts();
  const notificationsReceiversRef = React.useRef();
  const modalRoot = document.getElementById('dashboard-container') || document.body;

  const fetchData = async () => {
    const companyCustomers = await fetchCompanyCustomers();
    setCustomers(
      companyCustomers.map((customer) => ({
        label: customer.name,
        value: customer.id,
      }))
    );

    const companyCategories = await fetchCompanyCategories();
    setCategories(
      companyCategories.map((category) => ({
        label: category.name,
        value: category.id,
      }))
    );
  };

  React.useLayoutEffect(() => {
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, type } = e.target;
    const value = type === 'checkbox' ? e.target.checked : e.target.value;
    _.set(state, name, value);
    setState({ ...state });
  };

  const handleDuplicateFrom = (job) => {
    setState({
      ...state,
      customer: state.customer,
      category: job.category,
      sender: { email: job.sender?.email || '' },
      rules: job.rules,
      rulesLogic: job.rulesLogic || { passing: 'and', failing: 'and', warning: 'and' },
      hoursUntilWarning: job.hoursUntilWarning || '',
      emailCapacity: job.emailCapacity || '',
      notifications: {
        ...job.notifications,
        receivers: job.notifications?.receivers || [],
      },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setPreventSubmit(true);
      const response = await axios.post('/jobs/', {
        ...state,
        company: activeCompany,
        hoursUntilWarning:
          parseInt(state.hoursUntilWarning) || defaultJobSettings.hoursUntilWarning,
        emailCapacity: parseInt(state.emailCapacity) || defaultJobSettings.emailCapacity,
      });
      if (response.status === 201) {
        removeAllToasts();
        successToast('Monitor added successfully!');
        if (onClose) {
          onClose();
        }
        if (onSuccess) {
          onSuccess();
        }
        return;
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Add Monitor.' });
    }
    setPreventSubmit(false);
  };

  const handleRulesChange = (rules) => {
    setState((state) => ({ ...state, rules }));
  };

  const handleRulesLogicChange = (rulesLogic) => {
    setState((state) => ({ ...state, rulesLogic }));
  };

  const handleLoadDuplicateFromOptions = async (search, prevOptions) => {
    const top = 10;
    const skip = prevOptions.length;
    let options = [];
    let hasMore = false;

    try {
      const select =
        'name,jobs.id,jobs.name,jobs.customer,jobs.category,jobs.sender,jobs.rules,jobs.rulesLogic,jobs.hoursUntilWarning,jobs.emailCapacity,jobs.notifications';
      const response = await axios.get(
        `/customers/jobs-by-company/?$top=${top}&$skip=${skip}&$cid=${activeCompany}&$search=${search}&$select=${select}&$startWith=${initialCustomer}&$orderby=name&$order=asc`
      );

      if (response.status === 200) {
        hasMore = prevOptions.length + top < response.data.count;
        const jobsPerCustomer = response.data.value;
        options = jobsPerCustomer.map((customer) => ({
          label: customer.name,
          options: customer.jobs.map((job) => ({
            label: job.name,
            value: job.id,
            job,
          })),
        }));
      }
    } catch (err) {
      console.error(err);
    }

    return {
      options,
      hasMore,
    };
  };

  const handleAddCustomerSuccess = ({ data: customer }) => {
    customers.push({
      label: customer.name,
      value: customer._id,
    });
    setState({ ...state, customer: customer._id });
  };

  const handleAddCategorySuccess = ({ data: category }) => {
    categories.push({
      label: category.name,
      value: category._id,
    });
    setState({ ...state, category: category._id });
  };

  return (
    <>
      <Form onSubmit={handleSubmit} {...rest}>
        <Container mb="xs">
          <Title variant="darkGrey" fontWeight="500">
            Add new monitor
          </Title>
        </Container>
        <Container flexDirection="row" mb="s">
          <Container mr="s">
            <TextInput
              id="name"
              name="name"
              variant="minimal"
              placeholder="Monitor name"
              value={state.name}
              onChange={handleChange}
              pattern={ValidationPattern.extendedAlphanumeric}
              title="Please enter a valid monitor name"
              required
              autoFocus
            />
          </Container>
          <Container>
            <Select
              isSearchable
              id="customer"
              testId="customer"
              placeholder="Customer"
              caretColor="primary"
              variant="minimal"
              options={customers}
              value={state.customer}
              onChange={(option) =>
                handleChange({
                  target: {
                    name: 'customer',
                    value: option.value,
                  },
                })
              }
              components={{
                MenuFooter: (
                  <MenuFooter onClick={() => openModal('add-new-customer')}>
                    <IconButton icon="plus" variant="transparent" size="11px" mr="7px" />
                    Add new customer
                  </MenuFooter>
                ),
              }}
              descriptive
              required
            />
          </Container>
        </Container>
        <Container flexDirection="row" mb="s">
          <Container mr="s">
            <Select
              id="category"
              testId="category"
              placeholder="Category"
              caretColor="primary"
              variant="minimal"
              options={categories}
              value={state.category}
              onChange={(option) =>
                handleChange({
                  target: {
                    name: 'category',
                    value: option.value,
                  },
                })
              }
              components={{
                MenuFooter: (
                  <MenuFooter onClick={() => openModal('add-new-category')}>
                    <IconButton icon="plus" variant="transparent" size="11px" mr="7px" />
                    Add new category
                  </MenuFooter>
                ),
              }}
              descriptive
              required
            />
          </Container>
          <Container>
            <AsyncSelect
              id="duplicate-settings-from"
              placeholder="Duplicate settings from"
              caretColor="primary"
              variant="minimal"
              value={duplicateFrom}
              onLoadOptions={handleLoadDuplicateFromOptions}
              onChange={(option) => {
                const value = option?.value || '';
                setDuplicateForm(value);
                if (value) {
                  handleDuplicateFrom(option.job);
                }
              }}
              grouped
              isClearable
            />
          </Container>
        </Container>
        <Container mb="m">
          <TextInput
            name="sender.email"
            type="emailOrDot"
            variant="minimal"
            placeholder="Email sender"
            value={state.sender?.email}
            onChange={handleChange}
            required
          />
        </Container>
        <Container mb="m">
          <Label htmlFor="notifications" color="tertiary" fontWeight="bold" mb="xs">
            Notifications to
          </Label>
          <Container flexDirection="row" alignItems="center">
            <ToggleSwitch
              id="notifications"
              name="notifications.enabled"
              testId="notifications-toggle"
              checked={state.notifications?.enabled}
              onChange={(event) => {
                handleChange(event);
                if (event.target.checked) {
                  notificationsReceiversRef.current.focus();
                }
              }}
              mr="s"
            />
            <MultiTextInput
              ref={notificationsReceiversRef}
              variant="minimal"
              placeholder="Email receivers"
              value={state.notifications?.receivers}
              onChange={(values) =>
                handleChange({
                  target: {
                    name: 'notifications.receivers',
                    value: values,
                  },
                })
              }
              validateValues={(value) => {
                const regex = new RegExp(ValidationPattern.email, 'i');
                const isValid = regex.test(value);
                if (!isValid) {
                  errorToast('Please enter a valid email address.');
                }
                return isValid;
              }}
              disabled={!state.notifications?.enabled}
              required
            />
          </Container>
          {state.notifications?.enabled && (
            <Label variant="legend" fontSize="13px" mt="xs">
              Ps: please add at least one receiver in the field above then hit enter
            </Label>
          )}
        </Container>
        <Container mb="m">
          <JobRules
            rules={state.rules}
            rulesLogic={state.rulesLogic}
            onRulesChange={handleRulesChange}
            onRulesLogicChange={handleRulesLogicChange}
          />
        </Container>
        <Container flexDirection="row" mb="l">
          <Container mr="s">
            <Label htmlFor="hours-until-warning" color="tertiary" fontWeight="bold" mb="xs">
              Hours until warning (optional)
            </Label>
            <TextInput
              id="hours-until-warning"
              name="hoursUntilWarning"
              type="number"
              min="1"
              placeholder="Hours until warning"
              variant="minimal"
              value={state.hoursUntilWarning}
              onChange={handleChange}
            />
          </Container>
          <Container>
            <Label htmlFor="email-capacity" color="tertiary" fontWeight="bold" mb="xs">
              Email capacity
            </Label>
            <TextInput
              name="emailCapacity"
              type="number"
              min="1"
              placeholder="Email capacity"
              variant="minimal"
              value={state.emailCapacity}
              onChange={handleChange}
              required
            />
          </Container>
        </Container>
        <Container flexDirection="row" justifyContent="flex-end" mt="xxs">
          <Button
            variant="contrast-primary"
            width="180px"
            disabled={preventSubmit}
            onClick={onClose}
            boxShadow="none"
            mr="s"
          >
            Close
          </Button>
          <Button type="submit" width="180px" disabled={preventSubmit}>
            Add Monitor
          </Button>
        </Container>
      </Form>
      {ReactDom.createPortal(
        <Modal
          show={modal.isOpen('add-new-customer', 'add-new-category')}
          onClose={closeModal}
          position={ModalPosition.TOP_CENTER}
          top="25%"
          width="35%"
          maxWidth="600px"
          stopClickPropagation
        >
          {modal.isOpen('add-new-customer') && (
            <AddCustomer
              submitButtonText="Add customer"
              submitButtonWidth="35%"
              onClose={closeModal}
              onSuccess={handleAddCustomerSuccess}
            />
          )}
          {modal.isOpen('add-new-category') && (
            <AddCategory
              submitButtonText="Add category"
              submitButtonWidth="35%"
              onClose={closeModal}
              onSuccess={handleAddCategorySuccess}
            />
          )}
        </Modal>,
        modalRoot
      )}
    </>
  );
};
